import {formattedPropertyModel, intlShape, utils} from '@wallaby-ui/core';
import classNames from 'classnames';
import {format, isValid} from 'date-fns';
import {isEqual, isNil, pick} from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';
import ReactDatePicker from 'react-datepicker';
import {defineMessages, injectIntl} from 'react-intl';

import styles from './DatePicker.less';
import {I18N as I18NTextBox} from './TextBox';
import stylesAutoLayout from '../AutoLayout.less';
import {ContentProperty} from 'src/js/model/auto_layout_content_schema';
import {LayoutProperty} from 'src/js/model/auto_layout_layout_schema';
import {MetadataSchema} from 'src/js/model/auto_layout_metadata_schema';
import {KEYWORD_PROP_CONTENT_VALUE} from 'src/js/model/auto_layout_screen_data_helpers';

const I18N = defineMessages({
  PLACEHOLDER: {
    id: 'autoLayout.date.placeholder',
    defaultMessage: 'Set date...',
    description: 'placeholder in date picker input',
  },
  TODAY: {
    id: 'autoLayout.date.today',
    defaultMessage: 'Today',
    description: 'text for today button in date picker input',
  },
});

class DatePicker extends React.PureComponent {
  constructor(props) {
    super(props);
    const {data} = this.props;

    /* eslint-disable react/no-unused-state */
    this.state = {
      prevValue: null,
      value: data[KEYWORD_PROP_CONTENT_VALUE],
    };
    /* eslint-enable react/no-unused-state */

    this.handleChange = this.handleChange.bind(this);
    this.handleChangeDate = this.handleChangeDate.bind(this);
  }

  static getDerivedStateFromProps(props, state) {
    if (!isEqual(props.data[KEYWORD_PROP_CONTENT_VALUE], state.prevValue)) {
      return {
        value: props.data[KEYWORD_PROP_CONTENT_VALUE],
        prevValue: state.value,
      };
    }

    // Return null to indicate no change to state.
    return null;
  }

  handleChange(value) {
    const {onDataChange} = this.props;
    onDataChange(this.getOnDataChangeData(value));
  }

  handleChangeDate(value) {
    const {dateOnly} = this.props;
    let newValue = value;
    if (dateOnly && value && isValid(value)) {
      newValue = format(value, utils.DateUtils.ISO_DATE_ONLY_FORMAT);
    }
    this.setState({value: newValue});
    this.handleChange(newValue);
  }

  getOnDataChangeData(newData) {
    const {label} = this.props;
    return {
      ...pick(this.props, ['pathToData', 'data']),
      label,
      newData: {[KEYWORD_PROP_CONTENT_VALUE]: newData},
    };
  }

  getValue() {
    const {value} = this.state;
    return isNil(value) ? null : new Date(value);
  }

  getTextOnlyValue() {
    const {data, dateOnly, intl} = this.props;
    let formattedValue;
    if (
      data &&
      data[KEYWORD_PROP_CONTENT_VALUE] &&
      data[KEYWORD_PROP_CONTENT_VALUE] !== ''
    ) {
      const propDefinition = new formattedPropertyModel.FormattedProperty({
        type: dateOnly
          ? formattedPropertyModel.FORMATTED_PROPERTY_TYPE_DATE
          : formattedPropertyModel.DEFAULT_FORMATTED_PROPERTY_DATETIME_FORMAT,
      });
      formattedValue = formattedPropertyModel.FormattedProperty.formatValue(
        propDefinition,
        data[KEYWORD_PROP_CONTENT_VALUE],
        intl
      );
    } else {
      formattedValue = utils.TranslateUtils.translate(
        I18NTextBox.EMPTY_DATA,
        intl
      );
    }
    return formattedValue;
  }

  /* eslint-disable no-unused-vars */
  render() {
    const {hasError, intl, isReadOnly, isTextOnly} = this.props;
    const locale = utils.DateUtils.translateLocaleToDateFnsLocale(intl.locale);
    const dateFormat = utils.DateUtils.getLocaleDateFnsFormatPattern(
      intl.locale
    );
    const textOnlyValue = this.getTextOnlyValue();
    return isTextOnly ? (
      <span
        className={stylesAutoLayout.dlScreenComponentInput}
        title={textOnlyValue}
      >
        {textOnlyValue}
      </span>
    ) : (
      <div
        className={classNames(
          styles.dlDatePickerInputWrapper,
          stylesAutoLayout.dlScreenComponentInput,
          {[styles.dlValidationError]: hasError}
        )}
      >
        <ReactDatePicker
          className={styles.dlDatePickerInput}
          onChange={this.handleChangeDate}
          dateFormat={dateFormat}
          defaultValue={[]}
          disabled={isReadOnly}
          disabledKeyboardNavigation
          dropdownMode={'select'}
          locale={locale}
          peekNextMonth
          placeholderText={utils.TranslateUtils.translate(
            I18N.PLACEHOLDER,
            intl
          )}
          selected={this.getValue()}
          scrollableYearDropdown
          showYearDropdown
          showMonthDropdown
          todayButton={utils.TranslateUtils.translate(I18N.TODAY, intl)}
        />
        <i
          className={classNames('far fa-calendar-alt', styles.dlDatePickerIcon)}
        />
      </div>
    );
  }

  /* eslint-enable no-unused-vars */
}

DatePicker.displayName = 'DatePicker';

/* eslint-disable react/no-unused-prop-types */
DatePicker.propTypes = {
  content: PropTypes.instanceOf(ContentProperty).isRequired,
  dateOnly: PropTypes.bool,
  layout: PropTypes.instanceOf(LayoutProperty).isRequired,
  metadata: PropTypes.instanceOf(MetadataSchema).isRequired,
  screenId: PropTypes.string.isRequired,
  idSuffix: PropTypes.string,
  data: PropTypes.objectOf(PropTypes.any),
  pathToData: PropTypes.string,
  intl: intlShape.isRequired,
  isReadOnly: PropTypes.bool,
  isTextOnly: PropTypes.bool,
  inputName: PropTypes.string,
  label: PropTypes.string,
  expanded: PropTypes.bool,
  onDataChange: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
};

DatePicker.defaultProps = {
  data: {},
  dateOnly: false,
  idSuffix: '',
  pathToData: '',
  isReadOnly: false,
  isTextOnly: false,
  inputName: '',
  label: '',
  expanded: false,
  hasError: false,
};

export default injectIntl(DatePicker);
